import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Image, Video, Loader2, Download, X, Crown, Check, LogIn, Gift, Sparkles, Folder, ArrowLeft, Calendar, MapPin } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import api, { galleryApi, eventsApi, settingsApi } from '../lib/api';
import { usePublicAuth } from '../context/PublicAuthContext';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Bravo watermark component
const BravoWatermark = () => (
  <div className="absolute bottom-2 right-2 pointer-events-none">
    <img 
      src="https://customer-assets.emergentagent.com/job_eventshub-12/artifacts/irr175gc_Blanco-Horizontal.png"
      alt=""
      className="h-6 w-auto opacity-50"
      draggable="false"
    />
  </div>
);

// Retry-enabled image component for gallery
const RetryImage = ({ src, alt, className, onError: customOnError, ...props }) => {
  const [retryCount, setRetryCount] = useState(0);
  const [imgSrc, setImgSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const maxRetries = 2;
  const fallbackSrc = 'https://customer-assets.emergentagent.com/job_318b698c-05e0-45f0-aa51-d3d773434be0/artifacts/p0qxibr4_BETO%20FISCHER%20%282%29.svg';
  
  const handleError = (e) => {
    if (retryCount < maxRetries) {
      // Add a cache buster and retry
      const separator = src.includes('?') ? '&' : '?';
      const newSrc = `${src}${separator}_retry=${Date.now()}`;
      setRetryCount(prev => prev + 1);
      setImgSrc(newSrc);
    } else {
      // Max retries reached, use fallback
      setImgSrc(fallbackSrc);
      setIsLoading(false);
      if (customOnError) customOnError(e);
    }
  };
  
  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 bg-[#1A1A1A] animate-pulse flex items-center justify-center">
          <Loader2 size={24} className="text-[#A1A1AA] animate-spin" />
        </div>
      )}
      <img
        src={imgSrc}
        alt={alt}
        className={className}
        onLoad={() => setIsLoading(false)}
        onError={handleError}
        {...props}
      />
    </div>
  );
};

const GalleryPage = () => {
  const { user, isAuthenticated } = usePublicAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [driveGallery, setDriveGallery] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [settings, setSettings] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [processingPremium, setProcessingPremium] = useState(false);
  const [freeDownloadsStatus, setFreeDownloadsStatus] = useState(null);
  const [usingFreeDownload, setUsingFreeDownload] = useState(false);
  const [eventPhotos, setEventPhotos] = useState([]);

  // Load initial data - only events with photos
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, settingsRes] = await Promise.all([
          eventsApi.getPublic(true),
          settingsApi.getSitePublic()
        ]);
        
        // Filter events that have drive_folder_id (they have photos)
        // Sort by date descending (most recent first)
        const eventsWithPhotos = eventsRes.data
          .filter(e => e.drive_folder_id)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setEvents(eventsWithPhotos);
        setSettings(settingsRes.data);
        
        // Check if there's an event in URL params
        const eventId = searchParams.get('evento');
        if (eventId) {
          const event = eventsWithPhotos.find(e => e.id === eventId);
          if (event) {
            setSelectedEvent(event);
            loadEventPhotos(event);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    // Check for premium download in URL
    const downloadId = searchParams.get('download');
    const paymentFailed = searchParams.get('payment_failed');
    const paymentPending = searchParams.get('payment_pending');
    
    if (downloadId) {
      handlePremiumDownloadComplete(downloadId);
    }
    if (paymentFailed) {
      toast.error('El pago no se pudo completar');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (paymentPending) {
      toast.info('El pago está pendiente de confirmación');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Fetch free downloads status when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      fetchFreeDownloadsStatus();
    } else {
      setFreeDownloadsStatus(null);
    }
  }, [isAuthenticated]);

  const fetchFreeDownloadsStatus = async () => {
    try {
      const response = await api.get('/gallery/free-downloads-status');
      setFreeDownloadsStatus(response.data);
    } catch (error) {
      console.error('Error fetching free downloads status:', error);
    }
  };

  // Load photos for a specific event
  const loadEventPhotos = async (event) => {
    if (!event?.id) return;
    
    setLoadingPhotos(true);
    setEventPhotos([]);
    
    try {
      // First, try to get manually uploaded images from /gallery
      const manualResponse = await api.get(`/gallery?event_id=${event.id}`);
      let photos = [];
      
      if (manualResponse.data && Array.isArray(manualResponse.data)) {
        // Transform manual gallery items to match the expected format
        photos = manualResponse.data.map(item => ({
          id: item.id,
          name: item.caption || 'Foto',
          mimeType: 'image/jpeg',
          thumbnailUrl: item.url,
          fullUrl: item.url,
          isManual: true
        }));
      }
      
      // Only try Google Drive if no manual photos exist
      if (photos.length === 0) {
        try {
          const driveResponse = await api.get(`/gallery/drive?event_id=${event.id}`);
          if (!driveResponse.data.error && driveResponse.data.items?.length > 0) {
            photos = driveResponse.data.items;
          }
        } catch (driveError) {
          console.log('Google Drive gallery not available:', driveError);
        }
      }
      
      setEventPhotos(photos);
    } catch (error) {
      console.error('Error loading event photos:', error);
      toast.error('Error al cargar las fotos del evento');
    } finally {
      setLoadingPhotos(false);
    }
  };

  // Handle event folder click
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setSearchParams({ evento: event.id });
    loadEventPhotos(event);
  };

  // Handle back to folders
  const handleBackToFolders = () => {
    setSelectedEvent(null);
    setEventPhotos([]);
    setSearchParams({});
  };

  // Handle completed premium download
  const handlePremiumDownloadComplete = async (purchaseId) => {
    try {
      const response = await api.get(`/gallery/premium-download/${purchaseId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bravo_producciones_premium_${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('¡Descarga premium completada!');
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('El pago aún está pendiente de confirmación');
      } else {
        console.error('Error downloading premium:', error);
        toast.error('Error al descargar. Puedes acceder desde tu perfil.');
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Handle free download (with watermark)
  const handleFreeDownload = async (item) => {
    if (downloading) return;
    
    setDownloading(true);
    toast.info('Preparando descarga con marca de agua...');
    
    try {
      const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/gallery/download/${item.id}`;
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bravo_producciones_${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('¡Descarga completada!');
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Error al descargar');
    } finally {
      setDownloading(false);
    }
  };

  // Handle free HD download (for registered users)
  const handleFreeHDDownload = async (item) => {
    if (usingFreeDownload || !isAuthenticated) return;
    
    setUsingFreeDownload(true);
    toast.info('Preparando descarga HD gratuita...');
    
    try {
      const response = await api.post(`/gallery/free-download/${item.id}`, {}, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bravo_producciones_hd_${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('¡Descarga HD gratuita completada!');
      fetchFreeDownloadsStatus();
    } catch (error) {
      console.error('Error in free HD download:', error);
      if (error.response?.status === 403) {
        toast.error('Ya usaste tus 5 descargas gratuitas');
      } else {
        toast.error('Error al descargar');
      }
    } finally {
      setUsingFreeDownload(false);
    }
  };

  // Handle premium purchase
  const handlePremiumPurchase = async () => {
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para comprar');
      navigate('/cuenta', { state: { from: '/galeria' } });
      return;
    }
    
    if (!selectedMedia) return;
    
    setProcessingPremium(true);
    
    try {
      const response = await api.post('/gallery/purchase-premium-download', {
        file_id: selectedMedia.id,
        file_name: selectedMedia.name || `Imagen ${selectedMedia.id.slice(0, 8)}`,
        thumbnail_url: selectedMedia.thumbnail_url
      });
      
      if (response.data.init_point) {
        window.location.href = response.data.init_point;
      }
    } catch (error) {
      console.error('Error creating premium purchase:', error);
      if (error.response?.status === 401) {
        toast.error('Tu sesión expiró. Por favor inicia sesión nuevamente.');
        navigate('/cuenta', { state: { from: '/galeria' } });
      } else {
        toast.error('Error al procesar el pago. Intenta nuevamente.');
      }
    } finally {
      setProcessingPremium(false);
    }
  };

  // Format date for display
  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const premiumPrice = settings?.gallery?.premium_download_price || 550;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO 
        title={selectedEvent ? `${selectedEvent.title} - Galería` : "Galería"}
        description="Galería de fotos y videos de nuestros eventos. Descargá tus fotos en alta calidad."
        url="/galeria"
      />
      <Navbar />
      
      {/* Header */}
      <section className="pt-8 md:pt-32 pb-8 px-6 lg:px-12" data-testid="gallery-header">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {selectedEvent ? (
              <>
                <button 
                  onClick={handleBackToFolders}
                  className="flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors mb-4"
                  data-testid="back-to-folders"
                >
                  <ArrowLeft size={20} />
                  <span className="text-sm uppercase tracking-wider">Volver a eventos</span>
                </button>
                <h1 className="text-4xl lg:text-6xl font-black tracking-tighter uppercase mt-2 mb-4">
                  {selectedEvent.title}
                </h1>
                <div className="flex flex-wrap gap-4 text-[#A1A1AA] text-sm">
                  {selectedEvent.date && (
                    <span className="flex items-center gap-2">
                      <Calendar size={16} />
                      {formatEventDate(selectedEvent.date)}
                    </span>
                  )}
                  {selectedEvent.location && (
                    <span className="flex items-center gap-2">
                      <MapPin size={16} />
                      {selectedEvent.location}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <span className="text-[#52525B] text-xs font-mono uppercase tracking-widest">
                  Momentos
                </span>
                <h1 className="text-5xl lg:text-7xl font-black tracking-tighter uppercase mt-2 mb-6">
                  {settings?.gallery?.title || 'Galería'}
                </h1>
                <p className="text-[#A1A1AA] text-lg max-w-2xl">
                  {settings?.gallery?.description || 'Explorá las fotos de nuestros eventos. Seleccioná un evento para ver las fotos.'}
                </p>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* User benefit banner - only show in folders view */}
      {!selectedEvent && !isAuthenticated && (
        <section className="pb-8 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-yellow-500/10 to-yellow-600/5 border border-yellow-500/30 p-4 flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3">
                <Gift className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                <div>
                  <p className="text-yellow-500 font-bold text-sm">¡Beneficio exclusivo para usuarios registrados!</p>
                  <p className="text-yellow-500/70 text-xs">Descarga tus primeras 5 fotos en alta calidad GRATIS</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/cuenta')}
                size="sm"
                className="bg-yellow-500 text-black hover:bg-yellow-400 font-bold whitespace-nowrap"
              >
                Registrarse
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      {/* Free downloads counter for logged in users - show in photo view */}
      {selectedEvent && isAuthenticated && freeDownloadsStatus && (
        <section className="pb-8 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`border p-4 flex items-center gap-4 ${
                freeDownloadsStatus.remaining > 0 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-[#1A1A1A] border-[#27272A]'
              }`}
            >
              {freeDownloadsStatus.remaining > 0 ? (
                <>
                  <Sparkles className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <p className="text-green-500 font-medium text-sm">
                    Tienes {freeDownloadsStatus.remaining} descarga{freeDownloadsStatus.remaining !== 1 ? 's' : ''} HD gratis disponible{freeDownloadsStatus.remaining !== 1 ? 's' : ''}
                  </p>
                </>
              ) : (
                <>
                  <Gift className="w-5 h-5 text-[#52525B] flex-shrink-0" />
                  <p className="text-[#A1A1AA] text-sm">
                    Descargas gratuitas agotadas ({freeDownloadsStatus.used}/{freeDownloadsStatus.limit})
                  </p>
                </>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="pb-24 px-6 lg:px-12" data-testid="gallery-content">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div 
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-24"
              >
                <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
                <p className="mt-4 text-[#52525B]">Cargando galería...</p>
              </motion.div>
            ) : !selectedEvent ? (
              /* Event Folders Grid */
              <motion.div
                key="folders"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {events.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {events.map((event, index) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        onClick={() => handleEventClick(event)}
                        className="group cursor-pointer"
                        data-testid={`event-folder-${event.id}`}
                      >
                        <div className="relative aspect-[4/3] overflow-hidden border border-[#27272A] hover:border-[#D4AF37] transition-all duration-300">
                          {/* Event Cover Image */}
                          {event.image_url ? (
                            <img
                              src={event.image_url}
                              alt={event.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center">
                              <Folder size={48} className="text-[#27272A]" />
                            </div>
                          )}
                          
                          {/* Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                          
                          {/* Folder Icon */}
                          <div className="absolute top-4 right-4 bg-black/60 p-2 rounded">
                            <Folder size={20} className="text-[#D4AF37]" />
                          </div>
                          
                          {/* Event Info */}
                          <div className="absolute bottom-0 left-0 right-0 p-4">
                            <h3 className="text-white font-bold text-lg uppercase tracking-wide mb-1 group-hover:text-[#D4AF37] transition-colors">
                              {event.title}
                            </h3>
                            <div className="flex items-center gap-3 text-[#A1A1AA] text-xs">
                              {event.date && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={12} />
                                  {formatEventDate(event.date)}
                                </span>
                              )}
                              {event.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin size={12} />
                                  {event.location}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-24 border border-[#27272A]">
                    <Folder size={48} className="mx-auto mb-4 text-[#27272A]" />
                    <h3 className="text-2xl font-black uppercase mb-4">
                      Sin eventos
                    </h3>
                    <p className="text-[#A1A1AA]">
                      No hay eventos con fotos disponibles.
                    </p>
                  </div>
                )}
              </motion.div>
            ) : (
              /* Event Photos Grid */
              <motion.div
                key="photos"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {loadingPhotos ? (
                  <div className="flex flex-col items-center justify-center py-24">
                    <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
                    <p className="mt-4 text-[#52525B]">Cargando fotos...</p>
                  </div>
                ) : eventPhotos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {eventPhotos.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.5) }}
                        className="aspect-square overflow-hidden border border-[#27272A] hover:border-white transition-colors cursor-pointer group relative"
                        onClick={() => setSelectedMedia(item)}
                        onContextMenu={(e) => e.preventDefault()}
                        data-testid={`gallery-item-${item.id}`}
                      >
                        {item.type === 'image' ? (
                          <RetryImage
                            src={item.thumbnail_url}
                            alt="Bravo Producciones"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 pointer-events-none select-none"
                            loading="lazy"
                            draggable="false"
                            onContextMenu={(e) => e.preventDefault()}
                          />
                        ) : (
                          <div className="relative w-full h-full">
                            <RetryImage
                              src={item.thumbnail_url}
                              alt="Video"
                              className="w-full h-full object-cover pointer-events-none select-none"
                              loading="lazy"
                              draggable="false"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                              <Video size={32} className="text-white" />
                            </div>
                          </div>
                        )}
                        <BravoWatermark />
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-24 border border-[#27272A]">
                    <Image size={48} className="mx-auto mb-4 text-[#27272A]" />
                    <h3 className="text-2xl font-black uppercase mb-4">
                      Sin fotos
                    </h3>
                    <p className="text-[#A1A1AA]">
                      Este evento aún no tiene fotos disponibles.
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Media Viewer Modal */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-[#27272A] max-w-5xl p-0 overflow-hidden">
          <VisuallyHidden>
            <DialogTitle>Vista de imagen</DialogTitle>
          </VisuallyHidden>
          {selectedMedia && (
            <div className="relative">
              {/* Close button */}
              <button
                onClick={() => setSelectedMedia(null)}
                className="absolute top-4 right-4 z-20 bg-black/70 hover:bg-black p-2 transition-colors"
              >
                <X size={20} className="text-white" />
              </button>
              
              {/* Media content */}
              <div className="relative" onContextMenu={(e) => e.preventDefault()}>
                {selectedMedia.type === 'image' ? (
                  <>
                    <img
                      src={selectedMedia.url || selectedMedia.thumbnail_url?.replace('sz=w400', 'sz=w1600')}
                      alt="Bravo Producciones"
                      className="w-full h-auto max-h-[60vh] object-contain pointer-events-none select-none"
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    <div className="absolute bottom-4 right-4 pointer-events-none">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_eventshub-12/artifacts/irr175gc_Blanco-Horizontal.png"
                        alt=""
                        className="h-10 w-auto opacity-50"
                        draggable="false"
                      />
                    </div>
                  </>
                ) : (
                  <iframe
                    src={selectedMedia.url}
                    className="w-full h-[60vh]"
                    allow="autoplay"
                    allowFullScreen
                    title="Video Bravo Producciones"
                  />
                )}
              </div>
              
              {/* Download options - only for images */}
              {selectedMedia.type === 'image' && (
                <div className="p-6 bg-[#121212] border-t border-[#27272A]">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Free download with watermark */}
                    <div className="p-4 border border-[#27272A] bg-[#0A0A0A]">
                      <div className="flex items-center gap-2 mb-3">
                        <Download size={20} className="text-[#A1A1AA]" />
                        <h4 className="font-bold uppercase text-sm">Gratis</h4>
                      </div>
                      <ul className="text-xs text-[#A1A1AA] space-y-1 mb-4">
                        <li className="flex items-center gap-2">
                          <Check size={12} /> Con marca de agua
                        </li>
                        <li className="flex items-center gap-2">
                          <Check size={12} /> Calidad web
                        </li>
                      </ul>
                      <Button
                        onClick={() => handleFreeDownload(selectedMedia)}
                        disabled={downloading}
                        variant="outline"
                        className="w-full border-[#27272A] hover:bg-[#1A1A1A] text-xs"
                      >
                        {downloading ? (
                          <Loader2 size={14} className="mr-2 animate-spin" />
                        ) : (
                          <Download size={14} className="mr-2" />
                        )}
                        Descargar
                      </Button>
                    </div>
                    
                    {/* Free HD download for registered users */}
                    <div className={`p-4 border bg-[#0A0A0A] relative overflow-hidden ${
                      isAuthenticated && freeDownloadsStatus?.remaining > 0
                        ? 'border-green-500/50'
                        : 'border-[#27272A]'
                    }`}>
                      {isAuthenticated && freeDownloadsStatus?.remaining > 0 && (
                        <div className="absolute top-0 right-0 bg-green-500 text-black text-[10px] font-bold px-2 py-0.5 uppercase">
                          {freeDownloadsStatus.remaining} gratis
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-3">
                        <Gift size={20} className={isAuthenticated && freeDownloadsStatus?.remaining > 0 ? 'text-green-500' : 'text-[#52525B]'} />
                        <h4 className="font-bold uppercase text-sm">HD Gratis</h4>
                      </div>
                      <ul className="text-xs text-[#A1A1AA] space-y-1 mb-4">
                        <li className="flex items-center gap-2">
                          <Check size={12} className="text-green-500" /> Sin marca de agua
                        </li>
                        <li className="flex items-center gap-2">
                          <Check size={12} className="text-green-500" /> Alta calidad
                        </li>
                      </ul>
                      
                      {!isAuthenticated ? (
                        <Button
                          onClick={() => navigate('/cuenta', { state: { from: '/galeria' } })}
                          className="w-full bg-green-600 hover:bg-green-500 text-white font-bold text-xs"
                        >
                          <LogIn size={14} className="mr-2" />
                          Registrarse gratis
                        </Button>
                      ) : freeDownloadsStatus?.remaining > 0 ? (
                        <Button
                          onClick={() => handleFreeHDDownload(selectedMedia)}
                          disabled={usingFreeDownload}
                          className="w-full bg-green-600 hover:bg-green-500 text-white font-bold text-xs"
                        >
                          {usingFreeDownload ? (
                            <Loader2 size={14} className="mr-2 animate-spin" />
                          ) : (
                            <Sparkles size={14} className="mr-2" />
                          )}
                          Usar descarga gratis
                        </Button>
                      ) : (
                        <Button
                          disabled
                          variant="outline"
                          className="w-full border-[#27272A] text-[#52525B] text-xs cursor-not-allowed"
                        >
                          Agotadas
                        </Button>
                      )}
                    </div>
                    
                    {/* Premium download */}
                    <div className="p-4 border border-white bg-[#0A0A0A] relative overflow-hidden">
                      <div className="absolute top-0 right-0 bg-white text-black text-[10px] font-bold px-2 py-0.5 uppercase">
                        Ilimitado
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <Crown size={20} className="text-yellow-500" />
                        <h4 className="font-bold uppercase text-sm">Premium</h4>
                      </div>
                      <ul className="text-xs text-[#A1A1AA] space-y-1 mb-4">
                        <li className="flex items-center gap-2">
                          <Check size={12} className="text-yellow-500" /> Sin marca de agua
                        </li>
                        <li className="flex items-center gap-2">
                          <Check size={12} className="text-yellow-500" /> Máxima calidad
                        </li>
                      </ul>
                      
                      {isAuthenticated ? (
                        <Button
                          onClick={handlePremiumPurchase}
                          disabled={processingPremium}
                          className="w-full bg-white text-black hover:bg-gray-200 font-bold text-xs"
                        >
                          {processingPremium ? (
                            <Loader2 size={14} className="mr-2 animate-spin" />
                          ) : (
                            <Crown size={14} className="mr-2" />
                          )}
                          ${premiumPrice} ARS
                        </Button>
                      ) : (
                        <Button
                          onClick={() => navigate('/cuenta', { state: { from: '/galeria' } })}
                          className="w-full bg-white text-black hover:bg-gray-200 font-bold text-xs"
                        >
                          <LogIn size={14} className="mr-2" />
                          Iniciar sesión
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default GalleryPage;
